import React, { useCallback } from 'react'
import { intl } from 'lang/intl'

import { TtTrayChyronInfo, TtButton, TtIcon } from '@23/ui-react'

import {
  useOverlayState,
  setInfoChyron,
  removeOverlay,
} from '../../../../firebase/hooks/useOverlays'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'
import { useAppSelector } from '../../../../redux-types'
import { type PropsWithTrayItemChildren } from '../TrayItem'

interface ChyronInfoCardProps {
  id: string
}

const ChyronInfoCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<ChyronInfoCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlay = useOverlayState()
  const { name, live_date_epoch, thumbnail_small_download } = useAppSelector(
    (state) => state.webinar.liveData
  )

  const isChyronShowing =
    overlay?.type === ChyronType.WebinarInfo && overlay.cardId === id

  const showChyron = useCallback(() => {
    void setInfoChyron(name, live_date_epoch, thumbnail_small_download, id)
  }, [id, live_date_epoch, name, thumbnail_small_download])

  const onCardClickHandler = useCallback(() => {
    if (!isChyronShowing) {
      showChyron()
    }
    setActiveCard(id)
  }, [id, isChyronShowing, showChyron])

  if (card?.type !== 'chyron-info') return null

  return (
    <TtTrayChyronInfo
      tag={intl.formatMessage({ defaultMessage: 'Webinar info' })}
      name={name}
      date={intl.formatDate(new Date(+live_date_epoch * 1000), {
        dateStyle: 'full',
        timeStyle: 'short',
      })}
      imageUrl={thumbnail_small_download}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      <TtButton
        slot="toolbar"
        label={
          isChyronShowing
            ? intl.formatMessage({
                defaultMessage: 'Hide Webinar Info TvCard',
              })
            : intl.formatMessage({
                defaultMessage: 'Show Webinar Info TvCard',
              })
        }
        color="secondary"
        variant="ghost"
        size="x-small"
        circle
        onClick={isChyronShowing ? removeOverlay : showChyron}
      >
        <TtIcon
          library="one/webinars"
          name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
        />
      </TtButton>
      {children?.()}
    </TtTrayChyronInfo>
  )
}

export { ChyronInfoCard }
